import { useCallback, useEffect, useRef, useState } from 'react';
import { InfoCard, Progress, WarningPanel } from '@backstage/core-components';
import { useApi } from '@backstage/frontend-plugin-api';
import { Text } from '@backstage/ui';
import Button from '@material-ui/core/Button';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import {
  policyApiRef,
  type QuotaBudget,
  type QuotaPage,
  type QuotaQuantity,
} from '../../api';
import '../policyUi.css';
import { GroupObservationRefreshButton } from './GroupObservationRefreshButton';

type State =
  | { status: 'loading' }
  | { status: 'error'; error: string }
  | { status: 'ready'; page: QuotaPage; error?: string; loadingMore?: boolean };

function time(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Unknown' : date.toLocaleString();
}

// BigInt preserves the server's int64 quantity contract. Conversion only changes
// presentation; the server remains authoritative for remaining and overage.
export function formatQuotaValue(
  value: string,
  dimension: string,
  unit: string,
): string {
  const raw = BigInt(value);
  if (dimension === 'cpuMilli') {
    const negative = raw < 0n ? '-' : '';
    const absolute = raw < 0n ? -raw : raw;
    const fraction = (absolute % 1000n)
      .toString()
      .padStart(3, '0')
      .replace(/0+$/, '');
    return `${negative}${absolute / 1000n}${
      fraction ? `.${fraction}` : ''
    } vCPU`;
  }
  if (dimension === 'memoryBytes' || dimension === 'storageBytes') {
    return `${raw.toLocaleString()} bytes`;
  }
  return `${raw.toLocaleString()}${unit ? ` ${unit}` : ''}`;
}

function quantity(value: QuotaQuantity, budget: QuotaBudget): string {
  if (value.state === 'unknown')
    return `Unknown${value.reason ? `: ${value.reason}` : ''}`;
  if (value.state === 'notApplicable') return 'Not applicable';
  return value.value === undefined
    ? 'Unknown'
    : formatQuotaValue(value.value, budget.dimension, budget.unit);
}

function freshness(budget: QuotaBudget): string {
  const labels = budget.inventory.complete ? [] : ['Incomplete inventory'];
  const until = Date.parse(budget.inventory.freshUntil);
  if (Number.isNaN(until)) labels.push('Freshness unknown');
  else
    labels.push(until <= Date.now() ? 'Stale inventory' : 'Current inventory');
  return labels.join(', ');
}

export function quotaPressure(
  budget: QuotaBudget,
): 'over' | 'near' | 'normal' | 'unknown' {
  try {
    if (
      budget.overage.state === 'known' &&
      budget.overage.value !== undefined &&
      BigInt(budget.overage.value) > 0n
    )
      return 'over';
    if (
      budget.remaining.state !== 'known' ||
      budget.remaining.value === undefined
    )
      return 'unknown';
    const limit = BigInt(budget.limit);
    const remaining = BigInt(budget.remaining.value);
    if (limit <= 0n) return 'unknown';
    if (remaining < 0n) return 'over';
    return remaining * 5n <= limit ? 'near' : 'normal';
  } catch {
    return 'unknown';
  }
}

export function QuotaDashboard(props: {
  tenant: string;
  applicationGroupId?: string;
}) {
  const { tenant, applicationGroupId } = props;
  const api = useApi(policyApiRef);
  const [state, setState] = useState<State>({ status: 'loading' });
  const refreshController = useRef<AbortController>();
  const loadMoreController = useRef<AbortController>();

  const refresh = useCallback(async () => {
    refreshController.current?.abort();
    const request = new AbortController();
    refreshController.current = request;
    try {
      const page = await api.listQuotas(
        tenant,
        applicationGroupId,
        undefined,
        request.signal,
      );
      if (!request.signal.aborted) {
        setState(current => {
          if (
            current.status === 'ready' &&
            current.page.snapshotRevision === page.snapshotRevision &&
            JSON.stringify(current.page.items.slice(0, page.items.length)) ===
              JSON.stringify(page.items)
          ) {
            // Keep loaded pages only while the refreshed first page has the
            // same values. Time can make inventory stale without changing
            // the database-backed snapshot revision.
            return {
              status: 'ready',
              page: { ...current.page, evaluatedAt: page.evaluatedAt },
              loadingMore: current.loadingMore,
            };
          }
          return { status: 'ready', page };
        });
      }
      return true;
    } catch (error) {
      if (!request.signal.aborted) {
        const message = error instanceof Error ? error.message : String(error);
        setState(current =>
          current.status === 'ready'
            ? { ...current, error: message }
            : { status: 'error', error: message },
        );
      }
      return false;
    }
  }, [api, applicationGroupId, tenant]);

  useEffect(() => {
    setState({ status: 'loading' });
    let disposed = false;
    let failures = 0;
    let polling = false;
    let timer: number;
    const poll = async () => {
      if (disposed || polling) return;
      if (document.visibilityState !== 'visible') {
        timer = window.setTimeout(poll, 30_000);
        return;
      }
      polling = true;
      const success = await refresh();
      polling = false;
      if (disposed) return;
      failures = success ? 0 : Math.min(failures + 1, 4);
      timer = window.setTimeout(poll, 30_000 * 2 ** failures);
    };
    void poll();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        window.clearTimeout(timer);
        if (!polling) void poll();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      disposed = true;
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibility);
      refreshController.current?.abort();
      loadMoreController.current?.abort();
    };
  }, [refresh]);

  const loadMore = async () => {
    if (
      state.status !== 'ready' ||
      state.loadingMore ||
      !state.page.has_more ||
      !state.page.next_cursor
    )
      return;
    const previous = state.page;
    const request = new AbortController();
    loadMoreController.current = request;
    setState({ status: 'ready', page: previous, loadingMore: true });
    try {
      const next = await api.listQuotas(
        tenant,
        applicationGroupId,
        previous.next_cursor,
        request.signal,
      );
      if (!request.signal.aborted) {
        if (next.snapshotRevision !== previous.snapshotRevision) {
          void refresh();
        } else {
          setState(current =>
            current.status === 'ready' &&
            current.page.snapshotRevision === previous.snapshotRevision
              ? {
                  status: 'ready',
                  page: {
                    ...next,
                    items: [...current.page.items, ...next.items],
                  },
                }
              : current,
          );
        }
      }
    } catch (error) {
      if (!request.signal.aborted) {
        // A changed server snapshot invalidates every earlier page. Restart
        // rather than combining budgets observed at different revisions.
        if (
          typeof error === 'object' &&
          error !== null &&
          'statusCode' in error &&
          error.statusCode === 409
        ) {
          void refresh();
        } else {
          setState(current =>
            current.status === 'ready' &&
            current.page.snapshotRevision === previous.snapshotRevision
              ? {
                  ...current,
                  loadingMore: false,
                  error: error instanceof Error ? error.message : String(error),
                }
              : current,
          );
        }
      }
    }
  };

  return (
    <InfoCard
      title={
        applicationGroupId
          ? 'Application Group quotas'
          : `Tenant ${tenant} quotas`
      }
    >
      <Typography variant="body2">
        Each row is an independent budget. Values are the latest orchestrator
        snapshot, not a promise that a new request will be admitted.
      </Typography>
      {state.status === 'loading' && <Progress />}
      {state.status === 'error' && (
        <WarningPanel title="Quotas unavailable">{state.error}</WarningPanel>
      )}
      {state.status === 'ready' && (
        <>
          <Typography variant="body2">
            Snapshot evaluated: {time(state.page.evaluatedAt)}
          </Typography>
          {state.page.items.length === 0 && (
            <Typography>
              No active quota budgets were returned for this scope.
            </Typography>
          )}
          {state.page.items.some(
            budget => quotaPressure(budget) === 'unknown',
          ) && (
            <WarningPanel title="Quota usage cannot be verified">
              At least one budget has missing or stale inventory. Its current
              usage and overage are unknown until new resource observations are
              recorded. Do not treat this budget as within the limit.
            </WarningPanel>
          )}
          {state.page.items.length > 0 && (
            <Table size="small" aria-label="Policy quota budgets">
              <TableHead>
                <TableRow>
                  <TableCell>Budget</TableCell>
                  <TableCell>Limit</TableCell>
                  <TableCell>Allocated</TableCell>
                  <TableCell>Reserved</TableCell>
                  <TableCell>Remaining</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {state.page.items.map(budget => (
                  <TableRow
                    key={budget.budgetId}
                    className={`policy-quota-row--${quotaPressure(budget)}`}
                  >
                    <TableCell>
                      {budget.policyRef.id} / {budget.quotaId}
                      <Typography variant="caption" display="block">
                        {budget.scope.level === 'tenant' ? 'Tenant' : 'Group'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {formatQuotaValue(
                        budget.limit,
                        budget.dimension,
                        budget.unit,
                      )}
                    </TableCell>
                    <TableCell>{quantity(budget.allocated, budget)}</TableCell>
                    <TableCell>{quantity(budget.reserved, budget)}</TableCell>
                    <TableCell>{quantity(budget.remaining, budget)}</TableCell>
                    <TableCell>
                      {quotaPressure(budget) === 'over' && (
                        <Text as="strong" color="danger">
                          Over limit ·{' '}
                        </Text>
                      )}
                      {quotaPressure(budget) === 'near' && (
                        <Text as="strong" color="warning">
                          Near limit ·{' '}
                        </Text>
                      )}
                      {quotaPressure(budget) === 'unknown' && (
                        <Text as="strong" color="warning">
                          Usage unverified ·{' '}
                        </Text>
                      )}
                      {budget.mode} · {budget.enforcementState}
                      <br />
                      {freshness(budget)}
                      <Typography variant="caption" display="block">
                        Observed {time(budget.inventory.capturedAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <details>
                        <summary>View budget</summary>
                        <dl>
                          <dt>Policy revision</dt>
                          <dd>
                            {budget.policyRef.id} / {budget.policyRef.revision}
                          </dd>
                          <dt>Binding revision</dt>
                          <dd>
                            {budget.bindingRef.id} /{' '}
                            {budget.bindingRef.revision}
                          </dd>
                          <dt>Scope</dt>
                          <dd>
                            {budget.scope.level === 'tenant'
                              ? 'Tenant'
                              : `Application Group ${budget.scope.applicationGroupId}`}
                          </dd>
                          <dt>Applies to</dt>
                          <dd>
                            {budget.kinds.join(', ') || 'All kinds'} ·{' '}
                            {budget.stages.join(', ') || 'all stages'} ·{' '}
                            {budget.operations.join(', ') || 'all operations'}
                          </dd>
                          <dt>Overage</dt>
                          <dd>{quantity(budget.overage, budget)}</dd>
                          <dt>Inventory</dt>
                          <dd>
                            {freshness(budget)}; observed{' '}
                            {time(budget.inventory.capturedAt)}
                            {budget.inventory.unresolved.length > 0 &&
                              `; unresolved: ${budget.inventory.unresolved.join(
                                ', ',
                              )}`}
                          </dd>
                        </dl>
                        <details>
                          <summary>Raw budget JSON</summary>
                          <pre
                            style={{
                              whiteSpace: 'pre-wrap',
                              overflowWrap: 'anywhere',
                              maxHeight: 360,
                              overflow: 'auto',
                            }}
                          >
                            {JSON.stringify(budget, null, 2)}
                          </pre>
                        </details>
                      </details>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {state.error && (
            <WarningPanel title="More quotas could not be loaded">
              {state.error}
            </WarningPanel>
          )}
          <Button onClick={() => void refresh()}>Reload quota snapshot</Button>
          {state.page.has_more && (
            <Button
              disabled={state.loadingMore}
              onClick={() => void loadMore()}
            >
              {state.loadingMore ? 'Loading…' : 'Load more quotas'}
            </Button>
          )}
        </>
      )}
      {applicationGroupId && (
        <GroupObservationRefreshButton
          tenant={tenant}
          applicationGroupId={applicationGroupId}
        />
      )}
    </InfoCard>
  );
}
